import {
  MAPBOX_STATIC_IMAGE_BASE_URL,
  MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
  PROPERTY_MAP_COORDINATE_PRECISION,
  PROPERTY_MAP_SINGLE_MARKER_ZOOM,
} from 'components/Common/PropertyMap/constants'
import { clamp } from 'lib/maths/mathUtils'

export interface ExperiencesPassportMapSize {
  height: number
  width: number
}

export interface ExperiencesPassportMapViewport {
  centerLatitude: number
  centerLongitude: number
  zoom: number
}

const MAPBOX_TILE_SIZE = 512
const MAPBOX_MAX_LATITUDE = 85.0511
const MAPBOX_MAX_ZOOM = 22

function formatCoordinate(value: number) {
  return Number(value.toFixed(PROPERTY_MAP_COORDINATE_PRECISION)).toString()
}

function projectToWebMercator(latitude: number, longitude: number) {
  const safeLatitude = clamp(-MAPBOX_MAX_LATITUDE, latitude, MAPBOX_MAX_LATITUDE)
  const latitudeRadians = safeLatitude * Math.PI / 180
  const sinLatitude = Math.sin(latitudeRadians)

  return {
    x: (longitude + 180) / 360,
    y: 0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI),
  }
}

function unprojectWebMercatorLatitude(y: number) {
  return Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI
}

export function getExperiencesPassportMapViewport(
  markers: Array<App.MapMarker>,
  size: ExperiencesPassportMapSize,
  padding: number,
): ExperiencesPassportMapViewport | undefined {
  const firstMarker = markers[0]
  if (!firstMarker) return undefined

  if (markers.length === 1) {
    return {
      centerLatitude: firstMarker.latitude,
      centerLongitude: firstMarker.longitude,
      zoom: PROPERTY_MAP_SINGLE_MARKER_ZOOM,
    }
  }

  const projectedMarkers = markers.map(marker => projectToWebMercator(marker.latitude, marker.longitude))
  const firstProjectedMarker = projectedMarkers[0]
  const bounds = projectedMarkers.reduce((acc, marker) => ({
    minX: Math.min(acc.minX, marker.x),
    maxX: Math.max(acc.maxX, marker.x),
    minY: Math.min(acc.minY, marker.y),
    maxY: Math.max(acc.maxY, marker.y),
  }), {
    minX: firstProjectedMarker.x,
    maxX: firstProjectedMarker.x,
    minY: firstProjectedMarker.y,
    maxY: firstProjectedMarker.y,
  })
  const availableWidth = Math.max(1, size.width - padding * 2)
  const availableHeight = Math.max(1, size.height - padding * 2)
  const longitudeZoom = bounds.maxX === bounds.minX ? MAPBOX_MAX_ZOOM : Math.log2(availableWidth / (MAPBOX_TILE_SIZE * (bounds.maxX - bounds.minX)))
  const latitudeZoom = bounds.maxY === bounds.minY ? MAPBOX_MAX_ZOOM : Math.log2(availableHeight / (MAPBOX_TILE_SIZE * (bounds.maxY - bounds.minY)))
  const zoom = Number(clamp(0, Math.min(longitudeZoom, latitudeZoom, PROPERTY_MAP_SINGLE_MARKER_ZOOM), MAPBOX_MAX_ZOOM).toFixed(2))

  return {
    centerLatitude: unprojectWebMercatorLatitude((bounds.minY + bounds.maxY) / 2),
    centerLongitude: ((bounds.minX + bounds.maxX) / 2) * 360 - 180,
    zoom,
  }
}

export function projectCoordinatesToExperiencesPassportMap(
  latitude: number,
  longitude: number,
  viewport: ExperiencesPassportMapViewport,
  size: ExperiencesPassportMapSize,
) {
  const point = projectToWebMercator(latitude, longitude)
  const center = projectToWebMercator(viewport.centerLatitude, viewport.centerLongitude)
  const worldSize = MAPBOX_TILE_SIZE * 2 ** viewport.zoom

  return {
    x: size.width / 2 + (point.x - center.x) * worldSize,
    y: size.height / 2 + (point.y - center.y) * worldSize,
  }
}

export function buildExperiencesPassportStaticMapUrl(
  accessToken: string,
  markers: Array<App.MapMarker>,
  size: ExperiencesPassportMapSize,
  padding: number,
) {
  const viewport = getExperiencesPassportMapViewport(markers, size, padding)
  if (!viewport) return undefined

  const width = clamp(MAPBOX_STATIC_IMAGE_MIN_DIMENSION, Math.round(size.width), MAPBOX_STATIC_IMAGE_MAX_DIMENSION)
  const height = clamp(MAPBOX_STATIC_IMAGE_MIN_DIMENSION, Math.round(size.height), MAPBOX_STATIC_IMAGE_MAX_DIMENSION)
  const formattedViewport = `${formatCoordinate(viewport.centerLongitude)},${formatCoordinate(viewport.centerLatitude)},${viewport.zoom}`
  const searchParams = new URLSearchParams({ access_token: accessToken })

  return `${MAPBOX_STATIC_IMAGE_BASE_URL}/${formattedViewport}/${width}x${height}@2x?${searchParams.toString()}`
}
